<template>
  <SoPageWithRightSidePanel v-model="current.isSidePanelCollapsed">
    <div v-if="!current.billEligibility.isFetched">
      <SoSkeleton :height="64" />
      <SoSkeleton :type="['list-item-three-line', 'button']" :height="320" />
    </div>
    <SoFlow
      v-else
      :workflow-id="FlowType.IssueBill"
      :account-number="current.accountNumber"
      :start-step="flowStartStep"
      title="Issue a Bill"
      data-testid="issue-bill-flow"
      @completed="flowCompleted"
      @closed="flowClosed"
    />
    <template #sidePanel>
      <CustomerAccountInfoPanel></CustomerAccountInfoPanel>
    </template>
    <template #sidePanelRail>
      <CustomerAccountInfoRail></CustomerAccountInfoRail>
    </template>
  </SoPageWithRightSidePanel>
</template>

<script lang="ts">
  import { defineComponent } from "vue"
  import { useCurrentAccountStore } from "@/stores/currentAccount"
  import { useSnackbarStore } from "@/stores/snackbar"
  import { BillMutator } from "@/mutators/bill/BillMutator"
  import { errorReporter } from "@/services/ErrorService"
  import { RouteNames } from "@/types/router"
  import { FlowType } from "@/flows/useCases"
  import { BillIssue } from "@/services-handlers/getBillEligibility"
  import SoPageWithRightSidePanel from "@/components/templates/SoPageWithRightSidePanel.vue"
  import CustomerAccountInfoPanel from "../overview/CustomerAccountInfoPanel.vue"
  import CustomerAccountInfoRail from "../overview/CustomerAccountInfoRail.vue"
  import SoSkeleton from "@/components/SoSkeleton.vue"
  import SoFlow from "@/flows/SoFlow.vue"
  import TrackingService from "@/services/TrackingService"

  export default defineComponent({
    components: {
      SoPageWithRightSidePanel,
      CustomerAccountInfoPanel,
      CustomerAccountInfoRail,
      SoFlow,
      SoSkeleton,
    },
    data() {
      return {
        snackbar: useSnackbarStore(),
        current: useCurrentAccountStore(),
        issueBill: BillMutator.issueBill,
        FlowType,
      }
    },
    computed: {
      issues() {
        return this.current.billEligibility.data.issues
      },
      flowStartStep() {
        let flowStartStep = ""
        if (this.issues.includes(BillIssue.ACCOUNT_IN_REVIEW))
          flowStartStep = "accountInReview"
        else if (this.issues.includes(BillIssue.IS_COOLING_OFF))
          flowStartStep = "accountCoolingOff"
        else if (this.issues.includes(BillIssue.ACCOUNT_INACTIVE))
          flowStartStep = "accountInactive"
        else if (this.issues.includes(BillIssue.HAS_FAILED_BILL))
          flowStartStep = "alreadyFailedBill"
        else if (this.issues.includes(BillIssue.RECEIVED_BILL_TODAY))
          flowStartStep = "receivedBillToday"
        else if (this.issues.includes(BillIssue.NO_OPENING_READS))
          flowStartStep = "hasNoOpeningReads"
        else if (this.issues.includes(BillIssue.REQUIRES_READINGS))
          flowStartStep = "needRecentMeterReading"
        else flowStartStep = "readingsUpToDate"

        TrackingService.sendEvent("IssueBillStartStep:" + flowStartStep)

        return flowStartStep
      },
    },
    methods: {
      async flowCompleted() {
        const issueBill = this.current.submitIssueBill()
        try {
          await issueBill.mutateAsync()
          this.$router.push({
            name: RouteNames.CustomerIssueBillCompleted,
          })
        } catch (error) {
          this.$router.push({
            name: RouteNames.CustomerIssueBillFailed,
          })
          errorReporter.report(error)
          this.snackbar.addError(
            (error as { response: { data: { message: string } } })?.response
              ?.data.message,
          )
        }
      },
      flowClosed() {
        this.$router.push({
          name: RouteNames.CustomerFinancials,
        })
      },
    },
  })
</script>
